import { Fragment, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import BenchLeft from "../../assets/images/cms/bench-left.png";
import BenchRight from "../../assets/images/cms/bench-right.png";
import BenchCenter from "../../assets/images/cms/bench-center.png";
import AvtarOne from "../../assets/images/cms/boyStanding.svg";
import GirlAvtarStandImg from "../../assets/images/cms/girlStanding.svg";
import { getCharacterListAPI } from "../../api/user";
import { resolveCharacterUrlFromList } from "../../utils/charcter";
import {
  benchSpriteCharacters,
  centerBenchCharacterClassnames,
  centerBenchStandingPlayerWrapper,
  leftBenchCharacterClassnames,
  leftBenchStandingPlayerWrapper,
  rightBenchCharacterClassnames,
  rightBenchStandingPlayerWrapper,
  standingPlayerImageClass,
  standingPlayerImageClassCenterBench,
  standingPlayerImageClassRightBench,
  standingPlayerNameClass,
  type BenchLayout,
} from "./gameBenchConstants";

type LocalizedName = { en: string; ar: string };

export type GameResultLobbyParticipant = {
  _id?: string;
  full_name?: string;
  gender?: string | null;
  profile_image?: string | null;
  IQ?: number | null;
  IQ_old?: number | null;
  display_category_name?: LocalizedName | null;
  display_achievement_name?: LocalizedName | null;
  user_id?: {
    _id?: string;
    profile_image?: string | null;
    is_guest?: boolean;
    IQ?: number | null;
    IQ_old?: number | null;
    display_category_name?: LocalizedName | null;
    display_achievement_name?: LocalizedName | null;
  } | null;
};

export type GameResultLobbyTeam = {
  _id: string;
  team_name: string;
  team_number?: number;
  participants?: GameResultLobbyParticipant[];
};

const SLOTS_PER_TEAM = 8;
const SITTING_SLOTS_PER_BENCH = 7;
const BALANCED_SITTING_ORDER = [3, 2, 4, 1, 5, 0, 6] as const;

/** Winner +10%; losers use reciprocal so combined footprint stays similar (1.1 × 1/1.1 ≈ 1). */
const WINNER_BENCH_SCALE_CLASS = "origin-bottom z-30";
// const WINNER_BENCH_SCALE_CLASS = "scale-[1.1] origin-bottom z-30 mt-8";
// const LOSER_BENCH_SCALE_CLASS = "scale-[0.909] origin-bottom z-10 grayscale";
const LOSER_BENCH_SCALE_CLASS = "origin-bottom z-10 grayscale";

type ApiCharacter = { image?: string; gender?: string };

function pickWinningPoseSrc(
  profileImage: string | null | undefined,
  winningCharacters: ApiCharacter[],
  participantGender: string | null | undefined
): string | null {
  if (!winningCharacters.length) return null;
  const fromProfile = resolveCharacterUrlFromList(profileImage, winningCharacters as never[]);
  if (fromProfile) return fromProfile;
  const g = (participantGender || "").toLowerCase() === "female" ? "female" : "male";
  const byGender = g === "female" ? winningCharacters[5] : winningCharacters[2];
  return (byGender ?? winningCharacters[0])?.image ?? null;
}

function hasLobbyParticipant(p: GameResultLobbyParticipant | undefined): p is GameResultLobbyParticipant {
  return !!p && (!!p._id || !!p.full_name);
}

function getLocalizedName(obj: LocalizedName | null | undefined, lang: string): string {
  if (!obj) return "";
  return (obj as Record<string, string>)[lang] || obj.en || "";
}

function mergeParticipantFields(p: GameResultLobbyParticipant) {
  const profile_image = p.profile_image ?? p.user_id?.profile_image ?? null;
  const is_guest = p.user_id?.is_guest === true;
  const IQ = p.IQ ?? p.user_id?.IQ ?? null;
  const IQ_old = p.IQ_old ?? p.user_id?.IQ_old ?? null;
  const display_category_name = p.display_category_name ?? p.user_id?.display_category_name ?? null;
  const display_achievement_name = p.display_achievement_name ?? p.user_id?.display_achievement_name ?? null;
  return { profile_image, is_guest, IQ, IQ_old, display_category_name, display_achievement_name };
}

function getIQDelta(iq: number | null, iqOld: number | null) {
  if (typeof iq !== "number" || typeof iqOld !== "number") return null;
  const diff = iq - iqOld;
  if (diff === 0) return null;
  return { diff, isIncreased: diff > 0 };
}

/** Classes applied only to the character overlay for a bench (bench art stays put). Example: `"mt-2"`, `"-mt-1"`, `"2xl:mt-[0.5vw]"`. */
export type GameResultBenchCharacterLayerClasses = {
  row1Left?: string;
  row1Right?: string;
  center?: string;
  row2Left?: string;
  row2Right?: string;
};

export interface GameResultTeamLobbyProps {
  teams: GameResultLobbyTeam[];
  winnerTeamId: string | null;
  language: string;
  /** Per-bench tweaks to the character layer only (margin-top, translate, etc.). */
  characterLayerClassName?: GameResultBenchCharacterLayerClasses;
}

/** Full-row-wide coordinate space: parent is 50% viewport; overlay is 200% wide so %-positions match the original full-width bench layout. */
const halfRowCharacterLayerClass =
  "pointer-events-none absolute top-0 z-[5] w-[200%] min-h-[140px] sm:min-h-[160px] md:min-h-[200px] lg:min-h-[min(320px,55vw)] 2xl:min-h-[18vw]";

/** Bench rows use items-start (not items-end): with items-end, margin-top on one column grows the flex line and the other column re-aligns to the bottom, so both benches shift together. */
const benchRowFlexClass = "flex w-full flex-wrap items-start";

const GameResultTeamLobby = ({
  teams,
  winnerTeamId,
  language,
  characterLayerClassName,
}: GameResultTeamLobbyProps) => {
  const { data: sittingCharacterListData } = useQuery({
    queryKey: ["characterList", "sitting"],
    queryFn: () => getCharacterListAPI({ type: "sitting" }),
    staleTime: 1000 * 60 * 30,
  });
  const sittingCharacters = (sittingCharacterListData as { data?: { characters?: { image?: string }[] } })?.data
    ?.characters || [];

  const { data: standingCharacterListData } = useQuery({
    queryKey: ["characterList", "standing"],
    queryFn: () => getCharacterListAPI({ type: "standing" }),
    staleTime: 1000 * 60 * 30,
  });
  const standingCharacters = (standingCharacterListData as { data?: { characters?: { image?: string }[] } })?.data
    ?.characters || [];

  const { data: winningCharacterListData } = useQuery({
    queryKey: ["characterList", "winning"],
    queryFn: async () => {
      try {
        return await getCharacterListAPI({ type: "winning" });
      } catch {
        return { data: { characters: [] as ApiCharacter[] } };
      }
    },
    staleTime: 1000 * 60 * 30,
  });
  const winningCharacters =
    (winningCharacterListData as { data?: { characters?: ApiCharacter[] } })?.data?.characters ?? [];

  const orderedTeams = useMemo(() => {
    return [...teams].sort((a, b) => (a.team_number ?? 0) - (b.team_number ?? 0));
  }, [teams]);

  const isWinningTeam = (teamId: string) => !!winnerTeamId && winnerTeamId === teamId;

  const getTeamBenchScaleClass = (teamId: string | undefined) => {
    if (!winnerTeamId || !teamId) return "";
    return isWinningTeam(teamId) ? WINNER_BENCH_SCALE_CLASS : LOSER_BENCH_SCALE_CLASS;
  };

  const badgeSmallTextClass =
    "2xl:text-[0.42vw]/tight md:text-[8px]/tight sm:text-[6px]/tight text-[4px]/tight align-middle font-bold text-black-300";
  const badgeIqTextClass =
    "2xl:text-[0.85vw]/tight md:text-sm/tight sm:text-xs/tight text-[4px]/tight align-middle font-extrabold text-black-300";

  const sittingIqPillClass =
    `${badgeIqTextClass} inline-block 2xl:border-[0.052vw] border border-black-300 2xl:rounded-[0.20vw] rounded-sm shadow-[0px_1px_0px_0px_#110706] xl:px-[0.208vw] xl:py-[0.104vw] px-1 py-0.5 rotate-x-12 whitespace-nowrap`;

  const getBadgeParts = (p: GameResultLobbyParticipant) => {
    const { display_category_name, display_achievement_name, IQ, IQ_old, is_guest } = mergeParticipantFields(p);
    const categoryName = getLocalizedName(display_category_name, language);
    const badgeName = getLocalizedName(display_achievement_name, language);
    const hasHead = !!(categoryName || badgeName);
    const visibleIQ = is_guest ? null : IQ;
    const iqDelta = is_guest ? null : getIQDelta(visibleIQ, IQ_old);
    return { categoryName, badgeName, hasHead, IQ: visibleIQ, iqDelta };
  };

  /** Standing / inline: category, IQ on one line */
  const renderCategoryBadgeContent = (p: GameResultLobbyParticipant): ReactNode => {
    const { categoryName, badgeName, hasHead, IQ, iqDelta } = getBadgeParts(p);
    if (!hasHead && IQ == null) return null;
    return (
      <>
        {hasHead ? (
          <span className={badgeSmallTextClass}>
            {categoryName}
            {badgeName ? `${categoryName ? " " : ""}${badgeName}` : ""}
          </span>
        ) : null}
        {hasHead && IQ != null ? <span className={`${badgeSmallTextClass} mx-0.5`}>|</span> : null}
        {IQ != null ? (
          <span className={`${badgeIqTextClass} inline-flex items-center gap-0.5`}>
            IQ: {IQ}
            {iqDelta ? (
              <span className={iqDelta.isIncreased ? "text-green-700" : "text-red-600"}>
                {iqDelta.isIncreased ? "↑" : "↓"} {Math.abs(iqDelta.diff)}
              </span>
            ) : null}
          </span>
        ) : null}
      </>
    );
  };

  /** Sitting: IQ pill slightly above category pill (clearer over character head) */
  const renderSittingAchievementStack = (
    p: GameResultLobbyParticipant,
    femaleTint: boolean
  ): ReactNode => {
    const { categoryName, badgeName, hasHead, IQ, iqDelta } = getBadgeParts(p);
    if (!hasHead && IQ == null) return null;
    const tint = femaleTint ? "bg-[#97A4F2]!" : "bg-primary";
    return (
      <span className="flex flex-col items-center 2xl:gap-[0.2vw] gap-0.5 2xl:-translate-y-[0.45vw] -translate-y-1.5">
        {IQ != null ? (
          <span className={`${sittingIqPillClass} ${tint} inline-flex items-center gap-0.5`}>
            IQ: {IQ}
            {iqDelta ? (
              <span className={iqDelta.isIncreased ? "text-green-700" : "text-red-600"}>
                {iqDelta.isIncreased ? "↑" : "↓"} {Math.abs(iqDelta.diff)}
              </span>
            ) : null}
          </span>
        ) : null}
        {/* {hasHead ? (
          <span className={`${badgeSmallTextClass} inline-flex flex-wrap justify-center gap-0.5 max-w-full 2xl:border-[0.052vw] border border-black-300 2xl:rounded-[0.20vw] rounded-sm text-center ${tint} shadow-[0px_1px_0px_0px_#110706] xl:px-[0.208vw] xl:py-[0.104vw] px-1 py-0.5 rotate-x-12`}>
            <span>
              {categoryName}
              {badgeName ? `${categoryName ? " " : ""}${badgeName}` : ""}
            </span>
          </span>
        ) : null} */}
      </span>
    );
  };

  const renderBenchSeatSlots = (teamIndex: number, layout: BenchLayout) => {
    const team = orderedTeams[teamIndex];
    if (!team) return null;

    const teamScaleClass = getTeamBenchScaleClass(team._id);

    return Array.from({ length: SLOTS_PER_TEAM }).map((_, localIndex) => {
      const sittingOrderIndex = localIndex - 1;
      const sittingIdx =
        localIndex > 0
          ? BALANCED_SITTING_ORDER[sittingOrderIndex as keyof typeof BALANCED_SITTING_ORDER]
          : 0;
      const participant = team.participants?.[localIndex];
      const sittingSpriteIdx =
        localIndex > 0 ? ((sittingIdx as number) % SITTING_SLOTS_PER_BENCH) as keyof typeof benchSpriteCharacters : 0;
      const spriteSet = benchSpriteCharacters[sittingSpriteIdx];

      const getSittingCharCfg = () => {
        const si = sittingIdx as keyof typeof leftBenchCharacterClassnames;
        if (layout === "fullRowLeft") return leftBenchCharacterClassnames[si];
        if (layout === "fullRowRight") return rightBenchCharacterClassnames[si];
        return centerBenchCharacterClassnames[si as keyof typeof centerBenchCharacterClassnames];
      };

      if (!hasLobbyParticipant(participant)) {
        return <Fragment key={`bench-t${teamIndex}-s${localIndex}`} />;
      }

      const genderKey = participant.gender === "female" ? "female" : "male";
      const { profile_image } = mergeParticipantFields(participant);

      if (localIndex === 0) {
        const standingSrc = isWinningTeam(team._id)
          ? pickWinningPoseSrc(profile_image, winningCharacters, participant.gender) ||
          (participant.gender === "female" ? GirlAvtarStandImg : AvtarOne)
          : resolveCharacterUrlFromList(profile_image, standingCharacters as never[]) ||
          (participant.gender === "female" ? GirlAvtarStandImg : AvtarOne);

        const standingWrapperClass =
          layout === "fullRowLeft"
            ? leftBenchStandingPlayerWrapper
            : layout === "fullRowRight"
              ? rightBenchStandingPlayerWrapper
              : centerBenchStandingPlayerWrapper;

        const standingImgClass =
          layout === "fullRowRight"
            ? standingPlayerImageClassRightBench
            : layout === "centerBench"
              ? standingPlayerImageClassCenterBench
              : standingPlayerImageClass;

        const badgeContent = renderCategoryBadgeContent(participant);

        return (
          <div key={`bench-t${teamIndex}-s${localIndex}`} className={`${standingWrapperClass} ${teamScaleClass}`}>
            {badgeContent && !participant?.user_id?.is_guest ? (
              <span className="perspective-[250px] relative z-10 xl:mb-[0.5vw] md:px-4 px-1 mb-1 block w-full text-center">
                <span
                  className={`inline-block max-w-full 2xl:border-[0.052vw] border border-black-300 2xl:rounded-[0.20vw] rounded-sm bg-primary xl:px-[0.208vw] xl:py-[0.104vw] px-1 py-0.5 2xl:text-[0.42vw]/tight sm:text-[8px]/tight text-[4px]/tight font-bold text-black-300 shadow-[0px_1px_0px_0px_#110706] ${participant?.gender === "female" ? "bg-[#97A4F2]!" : "bg-primary"}`}
                >
                  {badgeContent}
                </span>
              </span>
            ) : <span className="perspective-[250px] relative z-10 xl:mb-[0.5vw] md:px-4 px-1 mb-1 block w-full text-center">
              <span
                className={`inline-block max-w-full 2xl:rounded-[0.20vw] rounded-sm bg-transparent xl:px-[0.208vw] xl:py-[0.104vw] px-1 py-0.5 2xl:text-[0.42vw]/tight sm:text-[8px]/tight text-[4px]/tight font-bold text-black-300`}
              >
              </span>
            </span>}

            <img src={standingSrc} alt="" className={standingImgClass} />
            <div className="yellow-wawe-shape light-wawe-shape absolute 2xl:-bottom-[3vw] xl:-bottom-[60px] lg:-bottom-12 -bottom-8 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 bg-center bg-[length:100%_100%] bg-no-repeat text-center 2xl:text-[0.625vw]/tight xl:text-xs/tight md:text-[9px]/tight text-[5px]/tight font-bold text-black-300 2xl:py-[0.3vw] py-0.5 landscape:max-lg:py-0.5 2xl:px-[0.208vw] sm:px-1 px-0.5 xl:max-w-[6.5vw] sm:max-w-[100px] max-w-[72px] 2xl:min-h-[1vw] md:min-h-8 sm:min-h-6 min-h-3 perspective-[250px] block text-center whitespace-normal break-words">
              <span className={standingPlayerNameClass}>{participant?.full_name}</span>
            </div>
          </div>
        );
      }

      const charCfg = getSittingCharCfg();

      const sittingStack = renderSittingAchievementStack(
        participant,
        participant?.gender === "female"
      );

      return (
        <div key={`bench-t${teamIndex}-s${localIndex}`} className={`${charCfg.div} ${teamScaleClass}`}>
          {sittingStack ? (
            <span className={charCfg.achivementWraper}>
              <span className="block max-w-full text-center">{sittingStack}</span>
            </span>
          ) : null}

          <img
            src={resolveCharacterUrlFromList(profile_image, sittingCharacters as never[]) || spriteSet[genderKey]}
            alt=""
            className={charCfg.image}
          />
          <span className={`${charCfg.name} relative flex items-center justify-center max-w-[160px]`}>
            <span className="block text-center wrap-break-word whitespace-normal line-clamp-2">
              {participant?.full_name.split(" ")[0]}
            </span>
          </span>
        </div>
      );
    });
  };

  const n = orderedTeams.length;
  if (n < 1) return null;

  const t2 = orderedTeams[2];
  const t3 = orderedTeams[3];

  const benchHalfImgClass =
    "w-full 2xl:h-[13.438vw] lg:h-[258px] md:h-[170px] sm:h-[120px] h-[87px] rtl:-scale-x-100";

  const t0 = orderedTeams[0];
  const t1 = orderedTeams[1];

  const layer = characterLayerClassName ?? {};

  return (
    <div className="flex w-full flex-col 2xl:mt-[4.5vw] md:mt-40 xs:mt-28 mt-20 landscape:max-lg:mt-35 2xl:px-[0.833vw] xl:px-10 px-4">
      <div className={`${benchRowFlexClass} pb-6 sm:pb-10`}>
        <div className="relative w-[50%] max-w-[50%] shrink-0 self-start overflow-visible">
          <img
            src={BenchLeft}
            alt=""
            className={`block w-full ${benchHalfImgClass} ${t0 ? getTeamBenchScaleClass(t0._id) : ""}`}
          />
          {n >= 1 && t0?.team_name && (
            <div className="absolute start-1/2 -translate-x-1/2 2xl:-top-[5vw] xl:-top-20 md:-top-18 -top-16 z-10 pointer-events-none">
              <p className="text-black-300 font-bold 2xl:text-[1.35vw]/tight xl:text-3xl/tight md:text-2xl/tight text-lg/tight text-center whitespace-nowrap">
                {t0.team_name}
              </p>
            </div>
          )}
          {n >= 1 && (
            <div
              className={`${halfRowCharacterLayerClass} start-0 ${layer.row1Left ?? ""}`}
            >
              {renderBenchSeatSlots(0, "fullRowLeft")}
            </div>
          )}
        </div>
        <div className="relative w-[50%] max-w-[50%] shrink-0 self-start overflow-visible">
          <img
            src={BenchRight}
            alt=""
            className={`block w-full ${benchHalfImgClass} ${t1 ? getTeamBenchScaleClass(t1._id) : ""}`}
          />
          {n >= 2 && t1?.team_name && (
            <div className="absolute start-1/2 -translate-x-1/2 2xl:-top-[5vw] xl:-top-20 md:-top-18 -top-16 z-10 pointer-events-none rtl:translate-x-1/2">
              <p className="text-black-300 font-bold 2xl:text-[1.35vw]/tight xl:text-3xl/tight md:text-2xl/tight text-lg/tight text-center whitespace-nowrap rtl:translate-x-0">
                {t1.team_name}
              </p>
            </div>
          )}
          {n >= 2 && (
            <div
              className={`${halfRowCharacterLayerClass} end-0 ${layer.row1Right ?? ""}`}
            >
              {renderBenchSeatSlots(1, "fullRowRight")}
            </div>
          )}
        </div>
      </div>

      {n === 3 && t2 && (
        <div className="flex w-full justify-center xl:mt-[6.5vw] md:mt-20 mt-30 xl:mb-[4vw] sm:mb-15 mb-10">
          <div className="relative w-full max-w-[70%] overflow-visible lg:max-w-fit 2xl:h-[10.938vw] lg:h-[210px] md:h-[138px] sm:h-[97px] h-[70px]">
            <img
              src={BenchCenter}
              alt=""
              className={`block h-full w-full object-contain ${getTeamBenchScaleClass(t2._id)}`}
            />
            <div className="absolute start-1/2 -translate-x-1/2 2xl:-top-[5vw] xl:-top-20 md:-top-18 -top-16 z-10 pointer-events-none">
              <p className="text-black-300 font-bold 2xl:text-[1.35vw]/tight xl:text-3xl/tight md:text-2xl/tight text-lg/tight text-center whitespace-nowrap">
                {t2.team_name}
              </p>
            </div>
            <div
              className={`pointer-events-none absolute inset-0 z-[5] min-h-full ${layer.center ?? ""}`}
            >
              {renderBenchSeatSlots(2, "centerBench")}
            </div>
          </div>
        </div>
      )}

      {n === 4 && t2 && t3 && (
        <div className={`${benchRowFlexClass} xl:mt-[7.5vw] mt-20`}>
          <div className="relative w-[50%] max-w-[50%] shrink-0 self-start overflow-visible">
            <img
              src={BenchLeft}
              alt=""
              className={`block w-full ${benchHalfImgClass} ${getTeamBenchScaleClass(t2._id)}`}
            />
            <div className="absolute start-1/2 -translate-x-1/2 2xl:-top-[5vw] xl:-top-20 md:-top-18 -top-16 z-10 pointer-events-none">
              <p className="text-black-300 font-bold 2xl:text-[1.35vw]/tight xl:text-3xl/tight md:text-2xl/tight text-lg/tight text-center whitespace-nowrap">
                {t2.team_name}
              </p>
            </div>
            <div
              className={`${halfRowCharacterLayerClass} start-0 ${layer.row2Left ?? ""}`}
            >
              {renderBenchSeatSlots(2, "fullRowLeft")}
            </div>
          </div>
          <div className="relative w-[50%] max-w-[50%] shrink-0 self-start overflow-visible">
            <img
              src={BenchRight}
              alt=""
              className={`block w-full ${benchHalfImgClass} ${getTeamBenchScaleClass(t3._id)}`}
            />
            <div className="absolute start-1/2 -translate-x-1/2 2xl:-top-[5vw] xl:-top-20 md:-top-18 -top-16 z-10 pointer-events-none rtl:translate-x-1/2">
              <p className="text-black-300 font-bold 2xl:text-[1.35vw]/tight xl:text-3xl/tight md:text-2xl/tight text-lg/tight text-center whitespace-nowrap rtl:translate-x-0">
                {t3.team_name}
              </p>
            </div>
            <div
              className={`${halfRowCharacterLayerClass} end-0 ${layer.row2Right ?? ""}`}
            >
              {renderBenchSeatSlots(3, "fullRowRight")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameResultTeamLobby;
